import React, { useCallback, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import toast from "react-hot-toast";

import Modal from "../modal/Modal";
import Textarea from "../form-item/Textarea";
import Toggle from "../form-item/Toggle";
import Button from "../button/Button";
import {
  useGenerateImagesMutation,
  type AIImageScene,
  type AIImageShape,
  type GeneratedImageCandidate,
} from "../../app/services/aiImage";

export interface AiImageGeneratorModalProps {
  /** 画像シーンキー（BE の scenes.py と対応） */
  scene: AIImageScene;
  /** プロジェクトID（image_project_banner / image_project_logo で必須） */
  projectId?: string | null;
  /** 作品タイトル（image_profile_work の文脈。任意） */
  workTitle?: string | null;
  /** 作品タグ（image_profile_work の文脈。任意） */
  workTags?: string[] | null;
  /** 作品説明（image_profile_work の文脈。任意） */
  workDesc?: string | null;
  /** 編集中の作品ID（既存アートワーク参照から除外。任意） */
  workId?: string | null;
  /** 対象の項目名（image_estimate_item の文脈。任意） */
  itemLabel?: string | null;
  /** 項目の補足説明（image_estimate_item の文脈。任意） */
  itemDesc?: string | null;
  /** 見積もり全体の項目名一覧（image_estimate_item の文脈。任意） */
  planItems?: string[] | null;
  /**
   * 出力サイズの形状。シーン既定の比率を上書きする（生成サイズと
   * プレビューの両方に効く）。正方形で使うサムネイルに "square" を渡す。
   */
  imageShape?: AIImageShape;
  /**
   * image_avatar: ユーザーが既にアバター写真を持っているか。
   * true のとき「現在の写真を元に生成」トグルを表示する。
   */
  hasCurrentAvatar?: boolean;
  /**
   * 候補プレビューボタンに付与する className。
   * ロゴシーンで `banner-color${n}` を渡すと、実際のバナー背景色・文字色の
   * 上で候補をプレビューできる。
   */
  candidateClassName?: string;
  /**
   * 候補の描画モード。"mask" は候補画像をマスクとして currentColor で塗る
   * （透過白ロゴを実際の表示色でプレビューするために使う）。デフォルト "image"。
   */
  candidateDisplay?: "image" | "mask";
  /** モーダルのタイトル（未指定ならシーンに応じた既定文言） */
  title?: string;
  /**
   * 候補から 1 枚選択して確定したときに呼ばれる。
   * 非同期処理（保存 API 呼び出し）を返すと、完了までボタンがローディングになる。
   * モーダルのクローズは呼び出し側が行う。
   */
  // eslint-disable-next-line no-unused-vars
  onSelect: (candidate: GeneratedImageCandidate) => void | Promise<void>;
  onClose: () => void;
}

/** 1 回の生成で並べる候補の枚数（BE の IMAGE_CANDIDATE_COUNT と揃える）。 */
const CANDIDATE_COUNT = 3;

const SCENE_TITLES: Record<AIImageScene, string> = {
  image_avatar: "AIでアバターを生成",
  image_project_banner: "AIでバナー画像を生成",
  image_project_logo: "AIでロゴを生成",
  image_profile_work: "AIでアートワークを生成",
  image_estimate_item: "AIでサムネイルを生成",
};

const SCENE_DESCRIPTIONS: Record<AIImageScene, string> = {
  image_avatar:
    "プロフィール情報をもとに、テイストの異なる3案（アーティスト写真風・ゲームキャラ風・動物キャラ風）を生成します。希望があれば入力してください。",
  image_project_banner:
    "プロジェクト情報をもとに、配信サービスのサムネイルのようなキービジュアル（文字なし）を生成します。希望があれば入力してください。",
  image_project_logo:
    "プロジェクト情報をもとに、コードネームの開発中ロゴ（単色・背景透過）を生成します。希望があれば入力してください。",
  image_profile_work:
    "作品名・タグ・説明と、登録済み作品のテイストをもとにアートワークを生成します。希望があれば入力してください。",
  image_estimate_item:
    "項目名・説明と見積もり全体の構成をもとに、ミニマルで抽象的なキービジュアルを生成します。希望があれば入力してください。",
};

// 現在のアバター写真を元に生成する場合はキャラ化ではなくアー写 3 段階になる
// （BE: IMAGE_AVATAR_PHOTO_STYLE_VARIANTS）。説明文もトグルに合わせて切り替える。
const AVATAR_PHOTO_DESCRIPTION =
  "現在のアバター写真を元に、ナチュラルなアーティスト写真からカラフルでポップなアーティスト写真まで、段階の異なる3案を生成します。希望があれば入力してください。";

const SCENE_PLACEHOLDERS: Record<AIImageScene, string> = {
  image_avatar:
    "例: 楽器を持った姿で、落ち着いた色味に（空欄でも生成できます）",
  image_project_banner:
    "例: 夜の都会をイメージした、青を基調とするシックな雰囲気（空欄でも生成できます）",
  image_project_logo:
    "例: 手書き風の筆文字で、力強い印象に（空欄でも生成できます）",
  image_profile_work:
    "例: 水彩画のような淡いグラデーションで幻想的に（空欄でも生成できます）",
  image_estimate_item:
    "例: 木製デスクの上の楽譜と鉛筆を、余白を活かして（空欄でも生成できます）",
};

// バナー / ロゴ / 見積もりサムネイルは横長プレビュー。他は正方形。
// `imageShape` を渡すとこの既定を上書きする。
const SCENE_ASPECT: Record<AIImageScene, string> = {
  image_avatar: "1 / 1",
  image_project_banner: "3 / 2",
  image_project_logo: "3 / 2",
  image_profile_work: "1 / 1",
  image_estimate_item: "3 / 2",
};

const SHAPE_ASPECT: Record<AIImageShape, string> = {
  square: "1 / 1",
  portrait: "2 / 3",
  landscape: "3 / 2",
};

const CandidateGrid = styled.div<{ $wide: boolean }>`
  display: grid;
  grid-template-columns: ${({ $wide }) =>
    $wide ? "1fr" : "repeat(3, 1fr)"};
  gap: 8px;
  width: 100%;
`;

const CandidateButton = styled.button<{
  $selected: boolean;
  $aspect: string;
  $hasCustomBackground: boolean;
}>`
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: ${({ $aspect }) => $aspect};
  /* candidateClassName（banner-color 系）が背景を持つ場合は上書きしない。
   * styled-components の宣言はグローバル CSS より後に注入され同一詳細度でも
   * 勝ってしまうため、カスタム背景時は background を宣言しない。 */
  ${({ $hasCustomBackground }) =>
    $hasCustomBackground
      ? ""
      : css`
          background: var(--soremo-background, #f0f0f0);
        `}
  border: 2px solid
    ${({ $selected }) =>
      $selected ? "var(--soremo-blue, #009ace)" : "var(--soremo-border)"};
  ${({ $selected }) =>
    $selected
      ? css`
          box-shadow: 0 0 0 2px rgba(0, 154, 206, 0.25);
        `
      : ""}
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

// 透過ロゴ候補を currentColor（banner-color の文字色）で塗ってプレビューする。
// className（banner-color${n}）由来の背景・color の上に重ねるため、
// 実際のバナー表示と同じ見え方になる。
const MaskedCandidate = styled.div<{ $url: string }>`
  width: 100%;
  height: 100%;
  padding: 12px;
  box-sizing: border-box;

  &::after {
    content: "";
    display: block;
    width: 100%;
    height: 100%;
    background-color: currentColor;
    mask-image: url("${({ $url }) => $url}");
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-image: url("${({ $url }) => $url}");
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
  }
`;

const pulse = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
`;

const GeneratingPlaceholder = styled.div<{ $aspect: string }>`
  aspect-ratio: ${({ $aspect }) => $aspect};
  border-radius: 8px;
  background: var(--soremo-background, #f0f0f0);
  animation: ${pulse} 1.6s ease-in-out infinite;
`;

/**
 * AI 画像生成の共通モーダル。
 *
 * 希望テキスト（任意。空でも生成可）を入力 → 候補を 3 枚生成 → 1 枚選択 →
 * 「この画像を使う」で `onSelect(candidate)` を呼ぶ。選択画像の保存
 * （PATCH /api/me / works CRUD / プロジェクト PATCH 等）は呼び出し側が行う。
 *
 * 生成は「1 枚ぶんのリクエスト × 枚数」を並列に投げ、返ってきた候補から
 * 順にスロットへ差し込む。1 リクエストで 3 枚まとめて待つより体感が速く、
 * 途中で 1 枚失敗しても残りは選べる。
 */
const AiImageGeneratorModal: React.FC<AiImageGeneratorModalProps> = ({
  scene,
  projectId,
  workTitle,
  workTags,
  workDesc,
  workId,
  itemLabel,
  itemDesc,
  planItems,
  imageShape,
  hasCurrentAvatar = false,
  candidateClassName,
  candidateDisplay = "image",
  title,
  onSelect,
  onClose,
}) => {
  const [generateImages] = useGenerateImagesMutation();

  const [hint, setHint] = useState("");
  /** 候補スロット。null は生成待ち（または生成に失敗した枠）。 */
  const [slots, setSlots] = useState<(GeneratedImageCandidate | null)[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [useCurrentAvatar, setUseCurrentAvatar] = useState(true);

  const isGenerating = pendingCount > 0;
  const hasCandidates = slots.some((slot) => slot !== null);
  const aspect = imageShape ? SHAPE_ASPECT[imageShape] : SCENE_ASPECT[scene];
  // 横長シーンは候補を 1 列で大きく、正方形シーンは 3 列で並べる
  const isWide = aspect !== "1 / 1";
  const showAvatarToggle = scene === "image_avatar" && hasCurrentAvatar;
  const description =
    showAvatarToggle && useCurrentAvatar
      ? AVATAR_PHOTO_DESCRIPTION
      : SCENE_DESCRIPTIONS[scene];

  const handleGenerate = useCallback(async () => {
    if (isGenerating) return;
    setSelectedKey(null);
    setSlots(Array.from({ length: CANDIDATE_COUNT }, () => null));
    setPendingCount(CANDIDATE_COUNT);

    let failures = 0;
    let firstError: string | null = null;

    await Promise.all(
      Array.from({ length: CANDIDATE_COUNT }, async (_unused, index) => {
        try {
          const res = await generateImages({
            scene,
            user_hint: hint.trim() || undefined,
            project_id: projectId ?? undefined,
            work_title: workTitle ?? undefined,
            work_tags: workTags ?? undefined,
            work_desc: workDesc ?? undefined,
            work_id: workId ?? undefined,
            item_label: itemLabel ?? undefined,
            item_desc: itemDesc ?? undefined,
            plan_items: planItems ?? undefined,
            image_shape: imageShape,
            candidate_count: 1,
            use_current_avatar:
              showAvatarToggle && !useCurrentAvatar ? false : undefined,
          }).unwrap();
          const candidate = res.data.candidates[0];
          if (!candidate) {
            failures += 1;
            return;
          }
          setSlots((prev) => {
            const next = [...prev];
            next[index] = candidate;
            return next;
          });
        } catch (error) {
          failures += 1;
          firstError =
            firstError ??
            (error as { data?: { message?: string } })?.data?.message ??
            null;
        } finally {
          setPendingCount((count) => count - 1);
        }
      }),
    );

    if (failures === CANDIDATE_COUNT) {
      toast.error(
        firstError ??
          "画像の生成に失敗しました。しばらく待ってからお試しください。",
      );
    } else if (failures > 0) {
      toast.error(`${failures}枚の生成に失敗しました`);
    }
  }, [
    generateImages,
    scene,
    hint,
    projectId,
    workTitle,
    workTags,
    workDesc,
    workId,
    itemLabel,
    itemDesc,
    planItems,
    imageShape,
    showAvatarToggle,
    useCurrentAvatar,
    isGenerating,
  ]);

  const handleConfirm = useCallback(async () => {
    const candidate = slots.find(
      (slot): slot is GeneratedImageCandidate =>
        slot !== null && slot.file_key === selectedKey,
    );
    if (!candidate) return;
    setIsApplying(true);
    try {
      await onSelect(candidate);
    } catch {
      toast.error("画像の保存に失敗しました。");
    } finally {
      setIsApplying(false);
    }
  }, [slots, selectedKey, onSelect]);

  return (
    <Modal
      title={title ?? SCENE_TITLES[scene]}
      description={description}
      size="default"
      onClose={onClose}
      primaryAction={{
        label: "この画像を使う",
        onClick: handleConfirm,
        disabled: !selectedKey || isApplying,
        loading: isApplying,
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
        disabled: isApplying,
      }}
    >
      <div className="u-col u-w100 u-gap16">
        <Textarea
          label="希望イメージ"
          option="optional"
          rows={3}
          maxLength={1000}
          placeholder={SCENE_PLACEHOLDERS[scene]}
          value={hint}
          onChange={(e) => setHint(e.target.value)}
        />
        {showAvatarToggle && (
          <Toggle
            value={useCurrentAvatar}
            onClick={(e) => {
              e.preventDefault();
              setUseCurrentAvatar((prev) => !prev);
            }}
          >
            <span className="bodytext-13 u-line-height-150">
              現在のアバター写真を元に生成する
            </span>
          </Toggle>
        )}
        <div className="u-row u-w100 u-justify-end">
          <Button
            size="small"
            theme="primary"
            variant={hasCandidates ? "outlined" : "filled"}
            onClick={handleGenerate}
            disabled={isGenerating || isApplying}
            leftIcon="auto_awesome"
          >
            {isGenerating
              ? "生成中..."
              : hasCandidates
                ? "再生成する"
                : "生成する"}
          </Button>
        </div>

        {slots.length > 0 && (
          <>
            <div className="bodytext-13 u-line-height-150">
              {isGenerating
                ? "画像を生成しています。できたものから順に表示します…"
                : "使用する画像を 1 枚選択してください。"}
            </div>
            <CandidateGrid $wide={isWide}>
              {slots.map((candidate, index) => {
                if (!candidate) {
                  // 生成が終わったのに埋まっていない枠は失敗したぶん。
                  // 待機アニメーションを出したままにしない。
                  return isGenerating ? (
                    <GeneratingPlaceholder
                      key={`pending-${index}`}
                      $aspect={aspect}
                    />
                  ) : null;
                }
                return (
                  <CandidateButton
                    key={candidate.file_key}
                    type="button"
                    className={candidateClassName}
                    $selected={candidate.file_key === selectedKey}
                    $aspect={aspect}
                    $hasCustomBackground={Boolean(candidateClassName)}
                    onClick={() => setSelectedKey(candidate.file_key)}
                    aria-pressed={candidate.file_key === selectedKey}
                  >
                    {candidateDisplay === "mask" ? (
                      <MaskedCandidate
                        $url={candidate.url}
                        role="img"
                        aria-label="生成された候補ロゴ"
                      />
                    ) : (
                      <img src={candidate.url} alt="生成された候補画像" />
                    )}
                  </CandidateButton>
                );
              })}
            </CandidateGrid>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AiImageGeneratorModal;
